import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Polygon, Polyline, useMap } from "@vis.gl/react-google-maps";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatIconSave } from "blocks-nextjs/blocks-react/components/Icons/Save/Save";
import { CatIconUndo } from "blocks-nextjs/blocks-react/components/Icons/Undo/Undo";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useGlobalStore from "../../store/useGlobalStore";
import { Variant } from "../GlobalNotification/GlobalNotification";
const MIN_POLYGON_POINTS = 3;
const MIN_POLYLINE_POINTS = 2;
const PREVIEW_POLYGON_OPTIONS = {
    fillColor: "#ff0000",
    fillOpacity: 0.2,
    strokeColor: "#ff0000",
    strokeOpacity: 0.8,
    strokeWeight: 2
};
const PolygonDrawingManager = ({ isDrawing, onClearPath, onSavePolygon }) => {
    const map = useMap();
    const [currentPath, setCurrentPath] = useState([]);
    const [cursorPosition, setCursorPosition] = useState(null);
    const { setGlobalNotification } = useGlobalStore((state) => ({
        setGlobalNotification: state.setGlobalNotification
    }));
    const clearCurrentPath = useCallback(() => {
        setCurrentPath([]);
        setCursorPosition(null);
        onClearPath?.();
    }, [onClearPath]);
    useEffect(() => {
        if (!map || !isDrawing) {
            return;
        }
        const clickListener = map.addListener("click", (event) => {
            const latLng = event.latLng;
            if (!latLng) {
                return;
            }
            setCurrentPath((previousPath) => [...previousPath, { lat: latLng.lat(), lng: latLng.lng() }]);
        });
        const moveListener = map.addListener("mousemove", (event) => {
            const latLng = event.latLng;
            if (!latLng) {
                return;
            }
            setCursorPosition({ lat: latLng.lat(), lng: latLng.lng() });
        });
        return () => {
            clickListener.remove();
            moveListener.remove();
        };
    }, [map, isDrawing]);
    // leaving drawing mode discards the in-progress shape so re-entering always starts clean
    useEffect(() => {
        if (isDrawing) {
            return;
        }
        setCursorPosition(null);
        if (currentPath.length > 0) {
            setCurrentPath([]);
            onClearPath?.();
        }
    }, [currentPath.length, isDrawing, onClearPath]);
    // crosshair cursor mirrors the pointer feedback of polygon drawing mode
    useEffect(() => {
        if (!map) {
            return;
        }
        map.setOptions({ draggableCursor: isDrawing ? "crosshair" : null });
        return () => map.setOptions({ draggableCursor: null });
    }, [map, isDrawing]);
    const handlePathsChanged = useCallback((paths) => {
        const [outerRing] = paths;
        if (!outerRing) {
            return;
        }
        setCurrentPath(outerRing.map((point) => ({ lat: point.lat(), lng: point.lng() })));
    }, []);
    const handleUndoLastPoint = useCallback(() => {
        setCurrentPath((previousPath) => previousPath.slice(0, -1));
    }, []);
    // the trailing line only guides the open path; the polygon renders its own edges once closed
    const previewLinePath = useMemo(() => {
        if (currentPath.length >= MIN_POLYGON_POINTS) {
            return [];
        }
        return cursorPosition ? [...currentPath, cursorPosition] : currentPath;
    }, [currentPath, cursorPosition]);
    const handleCompleteAndSave = useCallback(() => {
        if (currentPath.length < MIN_POLYGON_POINTS) {
            setGlobalNotification({
                message: `Please click at least ${MIN_POLYGON_POINTS} points to create a polygon.`,
                showNotification: true,
                type: Variant.warning
            });
            return;
        }
        onSavePolygon(currentPath);
        setCurrentPath([]);
        setCursorPosition(null);
    }, [currentPath, onSavePolygon, setGlobalNotification]);
    if (!isDrawing) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "absolute top-[70px] left-2.5 z-10 bg-white p-2.5 rounded shadow-md", "data-test": "polygon-drawer-controls", children: [_jsx("p", { className: "m-0 mb-1.5 text-xs text-gray-600", "data-test": "points-counter", children: currentPath.length === 0 ? (_jsx(_Fragment, { children: "Click on the map to add polygon points." })) : (_jsxs(_Fragment, { children: ["Points Added: ", _jsx("strong", { children: currentPath.length })] })) }), _jsxs("div", { className: "flex gap-1.5", children: [_jsxs(CatButton, { "data-test": "save-polygon-btn", disabled: currentPath.length < MIN_POLYGON_POINTS, onClick: handleCompleteAndSave, size: "sm", variant: "primary", children: ["Save Shape", _jsx(CatIconSave, { slot: "after" })] }), _jsxs(CatButton, { "data-test": "undo-point-btn", disabled: currentPath.length === 0, onClick: handleUndoLastPoint, size: "sm", variant: "ghost", children: ["Undo Point", _jsx(CatIconUndo, { slot: "after" })] }), _jsxs(CatButton, { "data-test": "clear-polygon-btn", disabled: currentPath.length === 0, onClick: clearCurrentPath, size: "sm", variant: "ghost", children: ["Clear", _jsx(CatIconUndo, { slot: "after" })] })] })] }), previewLinePath.length >= MIN_POLYLINE_POINTS && (_jsx(Polyline, { path: previewLinePath, strokeColor: PREVIEW_POLYGON_OPTIONS.strokeColor, strokeOpacity: PREVIEW_POLYGON_OPTIONS.strokeOpacity, strokeWeight: PREVIEW_POLYGON_OPTIONS.strokeWeight })), currentPath.length >= MIN_POLYGON_POINTS && (_jsx(Polygon, { editable: true, fillColor: PREVIEW_POLYGON_OPTIONS.fillColor, fillOpacity: PREVIEW_POLYGON_OPTIONS.fillOpacity, onPathsChanged: handlePathsChanged, paths: currentPath, strokeColor: PREVIEW_POLYGON_OPTIONS.strokeColor, strokeOpacity: PREVIEW_POLYGON_OPTIONS.strokeOpacity, strokeWeight: PREVIEW_POLYGON_OPTIONS.strokeWeight }))] }));
};
export default PolygonDrawingManager;
